import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { eachDayOfInterval, format, subDays, isEqual } from "date-fns";
import { InventoryResponse, Requirement, RoomInventoryResponse, AvailableRoomCount, Inventory } from "@estatex/types";
import { Observable } from "rxjs";
import { environment } from "@estatex/environments";
import { InventoryActions } from "@estatex/state";

@Injectable({
    providedIn: "root",
})
export class InventoryService {
    constructor(private httpClient: HttpClient, private ngrxStore: Store) {}
    private formatDate(date: Date): string {
        return format(date, "ddMMyyyy");
    }

    /**
     * Gets Inventory for specified requirement
     * @param checkinDate - checkin Date
     * @param checkoutDate - checkout Date
     * @returns Inventory Response
     */
    getInventoryForDates(checkinDate: Date, checkoutDate: Date, propertyId: number): Observable<InventoryResponse> {
        return this.httpClient.get<InventoryResponse>(`${environment.API_URL}/api/booking/inventory`, {
            params: {
                checkinDate: this.formatDate(checkinDate),
                checkoutDate: this.formatDate(checkoutDate),
                propertyId,
            },
        });
    }

    /**
     * Checks available inventory against requirement
     * @param requirement - Requirement object
     * @param inventoryResponse - Inventory Response Object
     * @returns - boolean indicating if we can book
     */
    checkInventoryAgainstRequirement(requirement: Requirement, inventoryResponse: InventoryResponse): boolean {
        const inventoryDates: Date[] = eachDayOfInterval({
            start: requirement.checkinDate,
            end: subDays(requirement.checkoutDate, 1),
        });

        // {
        //     DOUBLE_ROOM: [0, 1],
        //     KING_SIZE: [3]
        // }
        let availableRoomTypes: { [key: string]: number[] } = {};

        inventoryDates.forEach((date) => {
            Object.keys(inventoryResponse).forEach((roomType) => {
                Object.keys(inventoryResponse[roomType]).forEach((roomIndex, j) => {
                    let allMatchingInventoriesFound = false;

                    for (const inventory of inventoryResponse[roomType][roomIndex]) {
                        const idate = new Date(inventory.inventoryDate);
                        if (!isEqual(new Date(inventory.inventoryDate), date)) {
                            allMatchingInventoriesFound = false;
                            break;
                        }
                        allMatchingInventoriesFound = true;
                    }

                    if (allMatchingInventoriesFound) {
                        availableRoomTypes[roomType] = availableRoomTypes[roomType]?.length ? [...availableRoomTypes[roomType], j] : [j];
                    }
                });
            });
        });

        let finalInventoryResponse: InventoryResponse = {};

        let numberOfRooms = 0;

        // For any of the available room types check if the requirement's room is matching.
        Object.keys(availableRoomTypes).forEach((roomType) => {
            numberOfRooms += availableRoomTypes[roomType].length;
        });

        if (numberOfRooms < requirement.rooms) {
            return false;
        }

        let availableRooms: AvailableRoomCount = {};

        Object.keys(availableRoomTypes).forEach((roomType) => {
            let roomInventoryResponse: RoomInventoryResponse = {};
            // Rearrange the index
            let i = 0;
            availableRoomTypes[roomType].forEach((roomIndex) => {
                roomInventoryResponse[i++] = inventoryResponse[roomType][roomIndex];
            });

            availableRooms[roomType] = availableRoomTypes[roomType].length;

            finalInventoryResponse[roomType] = roomInventoryResponse;
        });

        this.ngrxStore.dispatch(InventoryActions.setAvailableRooms({ availableRooms }));
        this.ngrxStore.dispatch(InventoryActions.saveInventory({ inventory: finalInventoryResponse }));
        return true;
    }

    /**
     * Returns the available rooms for a date
     * @param date - for the requested date
     * @param propertyId - the property Id
     */
    getAvailableRooms(date: Date, propertyId?: number): Observable<number> {
        return this.httpClient.get<number>(`${environment.API_URL}/api/booking/inventory/available-rooms`, {
            params: {
                date: this.formatDate(date),
                propertyId: propertyId,
            },
        });
    }

    /**
     *
     * @param checkinDate - checkin Date
     * @param checkoutDate - checkout Date
     * @param roomType - Room Type Id
     * @param propertyId - Property Id
     * @returns
     */
    getInventoryForRoomType(checkinDate: Date, checkoutDate: Date, roomTypeId: number, propertyId: number): Observable<Inventory[]> {
        return this.httpClient.get<Inventory[]>(`${environment.API_URL}/api/booking/inventory/get-inventory-for-room-type`, {
            params: {
                checkinDate: this.formatDate(checkinDate),
                checkoutDate: this.formatDate(checkoutDate),
                roomTypeId,
                propertyId,
            },
        });
    }
}
